import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  FiCheckCircle,
  FiXCircle,
  FiSearch,
  FiFilter,
  FiRefreshCw,
  FiEye,
  FiChevronLeft,
  FiChevronRight,
  FiAlertCircle,
} from 'react-icons/fi';
import BaseUrl from '../../../Url/BaseUrl';
const OperatorApprovalsManagement = ({ onBack }) => {
  const navigate = useNavigate();
  const [pendingOperators, setPendingOperators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOperator, setSelectedOperator] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [approvingId, setApprovingId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Fetch pending operators
  const fetchPendingOperators = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${BaseUrl.baseurl}BusOperators/GetPending`);
      setPendingOperators(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch pending operators');
      console.error('Error fetching pending operators:', err);
    } finally {
      setLoading(false);
    }
  };

  // Approve operator
  const approveOperator = async (operator) => {
    try {
      setApprovingId(operator.operatorNumber);
      const updatedOperator = {
        ...operator,
        status: true // Set status to true for approval
      };

      await axios.put(`${BaseUrl.baseurl}BusOperators/${operator.operatorNumber}`, updatedOperator);
      
      // Remove from pending list
      setPendingOperators(prev => 
        prev.filter(op => op.operatorNumber !== operator.operatorNumber)
      );
      
      alert(`Operator ${operator.operatorName} approved successfully!`);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to approve operator');
      console.error('Error approving operator:', err);
    } finally {
      setApprovingId(null);
    }
  };

  // Reject operator (you might need a specific reject endpoint)
  const rejectOperator = async (operator) => {
    try {
      setRejectingId(operator.operatorNumber);
      // If there's a specific reject endpoint, use it
      // Otherwise, you can update status to false or delete
      const updatedOperator = {
        ...operator,
        status: false // Set status to false for rejection
      };

      await axios.put(`${BaseUrl.baseurl}BusOperators/${operator.operatorNumber}`, updatedOperator);
      
      // Remove from pending list
      setPendingOperators(prev => 
        prev.filter(op => op.operatorNumber !== operator.operatorNumber)
      );
      
      alert(`Operator ${operator.operatorName} rejected.`);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to reject operator');
      console.error('Error rejecting operator:', err);
    } finally {
      setRejectingId(null);
    }
  };

  // View operator details
  const viewOperatorDetails = (operator) => {
    setSelectedOperator(operator);
    setShowDetailsModal(true);
  };

  // Filter operators based on search term
  const filteredOperators = pendingOperators.filter(operator => {
    const searchLower = searchTerm.toLowerCase();
    return (
      operator.operatorName?.toLowerCase().includes(searchLower) ||
      operator.operatorNumber?.toLowerCase().includes(searchLower) ||
      operator.busOperatorContactNumber?.includes(searchLower) ||
      operator.stateCode?.toLowerCase().includes(searchLower) ||
      operator.zoneCode?.toLowerCase().includes(searchLower)
    );
  });

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentOperators = filteredOperators.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredOperators.length / itemsPerPage);

  useEffect(() => {
    fetchPendingOperators();
  }, []);

  // Format date if needed
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="management-page">
      <div className="management-page__header">
        <button className="btn-back" onClick={onBack}>
          <FiChevronLeft /> Back to Operators Management
        </button>
        <div className="management-page__title-section">
          <h1>Operator Approvals Management</h1>
          <p>Review and approve/reject pending bus operator registrations</p>
        </div>
      </div>

      <div className="management-page__content">
        {/* Stats Summary */}
        <div className="approval-stats">
          <div className="stat-card">
            <h3>Pending Approvals</h3>
            <p className="stat-number">{pendingOperators.length}</p>
          </div>
          <div className="stat-card">
            <h3>Total Reviewed</h3>
            <p className="stat-number">0</p>
          </div>
          <div className="stat-card">
            <h3>Approval Rate</h3>
            <p className="stat-number">0%</p>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="approval-controls">
          <div className="search-box">
            <FiSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search operators by name, number, or contact..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div className="control-buttons">
            <button className="btn-secondary" onClick={() => setSearchTerm('')}>
              <FiFilter /> Clear Filters
            </button>
            <button className="btn-secondary" onClick={fetchPendingOperators}>
              <FiRefreshCw /> Refresh
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="error-message">
            <FiAlertCircle /> {error}
            <button onClick={fetchPendingOperators}>Retry</button>
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading pending operators...</p>
          </div>
        ) : (
          <>
            {/* Operators Table */}
            <div className="approval-table-container">
              {currentOperators.length === 0 ? (
                <div className="empty-state">
                  <FiCheckCircle size="3em" />
                  <h3>No Pending Approvals</h3>
                  <p>All operator registrations have been reviewed.</p>
                </div>
              ) : (
                <>
                  <table className="approval-table">
                    <thead>
                      <tr>
                        <th>Operator Number</th>
                        <th>Operator Name</th>
                        <th>Contact Number</th>
                        <th>State Code</th>
                        <th>Zone Code</th>
                        <th>Website/App</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentOperators.map((operator) => (
                        <tr key={operator.operatorNumber}>
                          <td>
                            <span className="operator-id">
                              {operator.operatorNumber}
                            </span>
                          </td>
                          <td>
                            <strong>{operator.operatorName}</strong>
                          </td>
                          <td>{operator.busOperatorContactNumber || 'N/A'}</td>
                          <td>
                            <span className="badge state-badge">
                              {operator.stateCode || 'N/A'}
                            </span>
                          </td>
                          <td>
                            <span className="badge zone-badge">
                              {operator.zoneCode || 'N/A'}
                            </span>
                          </td>
                          <td>
                            <div className="link-group">
                              {operator.appLink && (
                                <a href={operator.appLink} target="_blank" rel="noopener noreferrer">
                                  App
                                </a>
                              )}
                              {operator.websiteContactLink && (
                                <a href={operator.websiteContactLink} target="_blank" rel="noopener noreferrer">
                                  Website
                                </a>
                              )}
                            </div>
                          </td>
                          <td>
                            <div className="action-buttons">
                              <button
                                className="btn-view"
                                onClick={() => viewOperatorDetails(operator)}
                              >
                                <FiEye /> View
                              </button>
                              <button
                                className="btn-approve"
                                onClick={() => approveOperator(operator)}
                                disabled={approvingId === operator.operatorNumber}
                              >
                                {approvingId === operator.operatorNumber ? (
                                  'Approving...'
                                ) : (
                                  <>
                                    <FiCheckCircle /> Approve
                                  </>
                                )}
                              </button>
                              <button
                                className="btn-reject"
                                onClick={() => rejectOperator(operator)}
                                disabled={rejectingId === operator.operatorNumber}
                              >
                                {rejectingId === operator.operatorNumber ? (
                                  'Rejecting...'
                                ) : (
                                  <>
                                    <FiXCircle /> Reject
                                  </>
                                )}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="pagination">
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                      >
                        <FiChevronLeft /> Previous
                      </button>
                      
                      <div className="page-numbers">
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                          <button
                            key={page}
                            className={currentPage === page ? 'active' : ''}
                            onClick={() => setCurrentPage(page)}
                          >
                            {page}
                          </button>
                        ))}
                      </div>
                      
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                      >
                        Next <FiChevronRight />
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </div>

      {/* Operator Details Modal */}
      {showDetailsModal && selectedOperator && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Operator Details</h2>
              <button className="modal-close" onClick={() => setShowDetailsModal(false)}>
                &times;
              </button>
            </div>
            
            <div className="modal-content">
              <div className="details-grid">
                <div className="detail-item">
                  <label>Operator Number:</label>
                  <span>{selectedOperator.operatorNumber}</span>
                </div>
                <div className="detail-item">
                  <label>Operator Name:</label>
                  <span>{selectedOperator.operatorName}</span>
                </div>
                <div className="detail-item">
                  <label>Contact Number:</label>
                  <span>{selectedOperator.busOperatorContactNumber || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>State Code:</label>
                  <span>{selectedOperator.stateCode || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Zone Code:</label>
                  <span>{selectedOperator.zoneCode || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>App Link:</label>
                  <a href={selectedOperator.appLink} target="_blank" rel="noopener noreferrer">
                    {selectedOperator.appLink || 'N/A'}
                  </a>
                </div>
                <div className="detail-item">
                  <label>Website Link:</label>
                  <a href={selectedOperator.websiteContactLink} target="_blank" rel="noopener noreferrer">
                    {selectedOperator.websiteContactLink || 'N/A'}
                  </a>
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowDetailsModal(false)}>
                Close
              </button>
              <div className="modal-actions">
                <button
                  className="btn-reject"
                  onClick={() => {
                    rejectOperator(selectedOperator);
                    setShowDetailsModal(false);
                  }}
                >
                  <FiXCircle /> Reject
                </button>
                <button
                  className="btn-approve"
                  onClick={() => {
                    approveOperator(selectedOperator);
                    setShowDetailsModal(false);
                  }}
                >
                  <FiCheckCircle /> Approve
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OperatorApprovalsManagement;